/**
 * MobileSurface — the page-level header and padding for a mobile dashboards
 * bucket.
 *
 * What this is NOT: chrome. It replaced `MobileChrome`, which also drew a
 * decorative status bar, an avatar and its own fixed bottom tab bar — all of
 * which now belong to the global nav (`shared/nav/NebariNav`) and appear on
 * every route rather than only these five surfaces. What's left here is the
 * per-surface title and subtitle, which are page content.
 */
import type { ReactNode } from 'react';

interface MobileSurfaceProps {
  /** Short page title or greeting (≤30 chars). */
  title: string;
  /** Optional muted line under the title (~1 line). */
  subtitle?: string;
  children: ReactNode;
}

export default function MobileSurface({ title, subtitle, children }: MobileSurfaceProps) {
  return (
    <div data-testid="mobile-surface" className="flex min-h-full flex-col">
      <div className="flex-none px-4 pb-3 pt-2">
        <div
          data-testid="surface-title"
          className="nb-display"
          style={{
            fontSize: 18,
            color: 'var(--nb-bark-dark)',
            letterSpacing: '0.01em',
            lineHeight: 1.1,
            textTransform: 'none',
          }}
        >
          {title}
        </div>
        {subtitle && (
          <div
            data-testid="surface-subtitle"
            className="mt-0.5 truncate text-[11px]"
            style={{ color: 'var(--nb-muted)' }}
          >
            {subtitle}
          </div>
        )}
      </div>
      <div className="min-h-0 flex-1 px-4 pb-4">{children}</div>
    </div>
  );
}
